import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveGoalsData } from '../data/goalsApi.js'
import { useHistoryStore } from '../store/historyStore.js'

/**
 * useGoalsCrud — add/delete goals and sub-goals at the goals-array level.
 *
 * Patterns mirror useUpdateGoal (same optimistic + fetch-fresh flow),
 * but for structural changes rather than field edits.
 *
 * Returns { addGoal, removeGoal, addSubgoal, removeSubgoal }.
 * Every call pushes a snapshot to the undo stack first so mistakes
 * can be reverted with ⌘Z or the toast.
 */
export function useGoalsCrud() {
  const queryClient = useQueryClient()

  const pushSnapshot = (label) => {
    useHistoryStore
      .getState()
      .pushSnapshot(
        label,
        queryClient.getQueryData(['goals']),
        queryClient.getQueryData(['meta'])
      )
  }

  const mutation = useMutation({
    mutationFn: async ({ updater }) => {
      // Read optimistic cache (updated by onMutate) and save that exact state
      const next = queryClient.getQueryData(['goals'])
      await saveGoalsData(next)
      return next
    },
    onMutate: async ({ updater, label }) => {
      await queryClient.cancelQueries({ queryKey: ['goals'] })
      const previous = queryClient.getQueryData(['goals'])
      pushSnapshot(label)
      queryClient.setQueryData(['goals'], (old) => updater(old || []))
      return { previous }
    },
    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['goals'], ctx.previous)
    },
  })

  // ─── Pillar-level fields (e.g. description) ─────────────────────────

  const updatePillar = ({ pillarName, field, value }) => {
    mutation.mutate({
      label: `Edit ${pillarName} ${field}`,
      updater: (data) =>
        data.map((p) =>
          p.pillar === pillarName ? { ...p, [field]: value } : p
        ),
    })
  }

  // ─── Goal CRUD ──────────────────────────────────────────────────────

  const addGoal = ({ pillarName, goal }) => {
    mutation.mutate({
      label: `Add goal to ${pillarName}`,
      updater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? { ...p, goals: [...(p.goals || []), goal] }
            : p
        ),
    })
  }

  const removeGoal = ({ pillarName, goalIndex }) => {
    mutation.mutate({
      label: `Remove goal from ${pillarName}`,
      updater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).filter((_, i) => i !== goalIndex),
              }
            : p
        ),
    })
  }

  // ─── Sub-goal CRUD ──────────────────────────────────────────────────

  const addSubgoal = ({ pillarName, goalIndex, subgoal }) => {
    mutation.mutate({
      label: `Add sub-goal`,
      updater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).map((g, i) =>
                  i === goalIndex
                    ? { ...g, subgoals: [...(g.subgoals || []), subgoal] }
                    : g
                ),
              }
            : p
        ),
    })
  }

  const removeSubgoal = ({ pillarName, goalIndex, subgoalIndex }) => {
    mutation.mutate({
      label: `Remove sub-goal`,
      updater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).map((g, i) =>
                  i === goalIndex
                    ? {
                        ...g,
                        subgoals: (g.subgoals || []).filter(
                          (_, j) => j !== subgoalIndex
                        ),
                      }
                    : g
                ),
              }
            : p
        ),
    })
  }

  return {
    addGoal,
    removeGoal,
    addSubgoal,
    removeSubgoal,
    updatePillar,
    isLoading: mutation.isPending,
  }
}
